import React, { useState, useEffect } from "react";
import {
  Sparkles,
  Pencil,
  Send,
  CheckCircle,
  Settings,
  FileText,
  Ban,
  CalendarX,
  History,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import AIProfileModal from "./AIProfileModal";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";
import { Button } from "../../components/ui";
import SplitActionButton from "../../components/ui/SplitActionButton";
import {
  selectHasValidAiSetting,
  selectHasValidTopic,
  selectProjectLoading,
  selectHasPostsToApprove,
  selectSocialPosts,
} from "../selectors/aiContentSelectors";
import { useQuery } from "../../AppLayout";
import RuleAction from "../../actions/RuleAction";
import AiContentTokenUsageBar from "./AiContentTokenUsageBar";




interface PromptHeaderProps {
  topic: string;
  setTopic: (topic: string) => void;
  onGenerate: () => void;
  onApproveAll: () => void;
  onUseSuggestedTopics: () => void;
  onClearPosts: () => void;
  onUnpublishAll: () => void;
  onClearDraft: () => void;
  totalCount?: number;
  successCount?: number;
  failedCount?: number;
}

const PromptHeader: React.FC<PromptHeaderProps> = ({
  topic,
  setTopic,
  onGenerate,
  onApproveAll,
  onUseSuggestedTopics,
  onUnpublishAll,
  onClearDraft,
  totalCount = 0,
  successCount = 0,
  failedCount = 0,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const appId = useQuery().get("appId");
  const view = useQuery().get("view");

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [showTopicSuggestConfirm, setShowTopicSuggestConfirm] = useState(false);
  const [hasInputError, setHasInputError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const hasShownProfileConfirm = React.useRef(false);
  const hasShownTopicConfirm = React.useRef(false);
  // Check if input is valid
  const isValidInput = topic.trim().length > 0;

  const projectLoading = useSelector(selectProjectLoading);
  const hasAiProfile = useSelector(selectHasValidAiSetting);
  const hasValidTopic = useSelector(selectHasValidTopic);
  const hasPostsToApprove = useSelector(selectHasPostsToApprove);
  const socialPosts = useSelector(selectSocialPosts);

  const stats = React.useMemo(() => {
    const posts = Array.isArray(socialPosts) ? socialPosts : [];
    const total = posts.length;
    let success = 0;
    let failed = 0;

    for (const post of posts) {
      const raw = post?.data?.status?.value || post?.status?.value || "";
      const status = String(raw).toUpperCase();
      if (status === "PUBLISHED") {
        success += 1;
      } else if (status === "FAILED" || status === "CANCELLED") {
        failed += 1;
      }
    }

    return { total, success, failed };
  }, [socialPosts]);

  useEffect(() => {
    if (projectId && appId && view === "profile") {
      navigate(`/workspaces/${projectId}/ai-content?key=AI%20Content`);
      dispatch(RuleAction.setDefaultPageApp(appId));
      setIsProfileModalOpen(true);
    }
  }, [projectId, view, appId, dispatch, navigate]);

  useEffect(() => {
    if (!hasAiProfile && !hasShownProfileConfirm.current && !projectLoading) {
      setIsProfileModalOpen(true);
      hasShownProfileConfirm.current = true;
    }
  }, [hasAiProfile, projectLoading]);

  useEffect(() => {
    if (hasAiProfile && !hasValidTopic && !hasShownTopicConfirm.current) {
      hasShownTopicConfirm.current = true;
      setShowTopicSuggestConfirm(true);
    }
  }, [hasAiProfile, hasValidTopic]);

  const handleUseSuggestedTopics = () => {
    if (!hasAiProfile) {
      setIsProfileModalOpen(true);
      hasShownProfileConfirm.current = true;
    } else {
      onUseSuggestedTopics();
    }
  };

  const handleProfileSave = () => {
    setIsProfileModalOpen(false);
    hasShownProfileConfirm.current = false;
  };

  const handleOpenProfileModal = () => {
    setIsProfileModalOpen(true);
  };

  const handleGenerate = () => {
    // Validate input before proceeding
    if (!isValidInput) {
      setHasInputError(true);
      // Focus input and scroll to it
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 100);
      return;
    }

    // Reset error state
    setHasInputError(false);

    if (!hasAiProfile) {
      setIsProfileModalOpen(true);
      hasShownProfileConfirm.current = true;
    } else {
      onGenerate();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTopic(e.target.value);
    // Clear error when user starts typing
    if (hasInputError) {
      setHasInputError(false);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  const getBorderColor = () => {
    if (hasInputError) return "border-[#D54033] border-1";
    if (isFocused) return "border-blue-500 border-1";
    if (isHovered) return "border-gray-300 border";
    return "border-gray-200 border";
  };

  const getIconColor = () => {
    if (hasInputError) return "text-[#D54033]";
    if (isFocused) return "text-blue-500";
    return "text-gray-500";
  };

  return (
    <div className="bg-white border-b border-gray-200 overflow-visible">
      <div className="flex flex-col p-4 sm:p-5 lg:p-5">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 sm:gap-4 lg:gap-6">
          <div className="flex-1 min-w-0">
            <div
              className={`bg-white rounded-lg h-[40px] transition-all duration-200 ${getBorderColor()}`}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className="flex items-center w-full h-full">
                <div className="flex items-center gap-2 sm:gap-[10px] px-3 sm:px-4 h-full flex-shrink-0">
                  <Pencil className={`w-4 h-4 transition-colors ${getIconColor()}`} />
                </div>
                <div className="flex-1 flex items-center px-3 sm:px-4 h-full min-w-0">
                  <input
                    ref={inputRef}
                    className="w-full text-sm sm:text-[15px] font-medium text-gray-800 outline-none placeholder:text-gray-400 bg-transparent"
                    placeholder={t(
                      "Describe your content topic (e.g., 'Best brand marketing strategy for success')"
                    )}
                    value={topic}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-row gap-2 items-center lg:ml-4 flex-shrink-0">
            <Button
              variant="primary"
              size="md"
              onClick={handleGenerate}
              className={`flex items-center justify-center gap-2 sm:gap-[10px] ${
                !isValidInput ? "opacity-60 cursor-not-allowed" : ""
              }`}
              leftIcon={<Send className="w-[14px] h-[14px] text-white flex-shrink-0" />}
            >
              {t("Generate")}
            </Button>
            {hasPostsToApprove && (
              <SplitActionButton
                anchor="bottom end"
                buttonClassName="h-[40px] rounded-full !bg-teal-500 hover:!bg-teal-600"
                actionButtons={[
                  {
                    text: t("Approve All to Publish"),
                    onClick: onApproveAll,
                    icon: <CheckCircle className="w-3.5 h-3.5 text-gray-700" />,
                  },
                  {
                    text: t("Unschedule All") as any,
                    onClick: onUnpublishAll,
                    icon: <History className="w-3.5 h-3.5 text-gray-700" />,
                  },
                  {
                    text: t("Clear Draft") as any,
                    onClick: onClearDraft,
                    icon: <CalendarX className="w-3.5 h-3.5 text-gray-700" />,
                  },
                ]}
              />
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-2 sm:gap-3 mt-3 sm:mt-4">

          <Button
            variant="secondary"
            size="sm"
            onClick={handleUseSuggestedTopics}
            className="flex items-center gap-1 bg-white hover:bg-gray-100 px-[6px] py-[6px] rounded-[6px]"
            leftIcon={<Sparkles className="w-3 h-3 text-[#1F2937] flex-shrink-0" />}
          >
            <span className="text-[12px] text-[#1F2937] leading-[16px]">
              {t("Use AI Suggested Topics")}
            </span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleOpenProfileModal}
            className="flex items-center gap-1 bg-white hover:bg-gray-100 ml-0 sm:ml-5 px-[6px] py-[6px] rounded-[6px]"
            leftIcon={<Settings className="w-3 h-3 text-[#1F2937] flex-shrink-0" />}
          >
            <span className="text-[12px] text-[#1F2937] leading-[16px]">{t("Update Profile")}</span>
          </Button>

          <AiContentTokenUsageBar />

          {/* Statistics Badges */}
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto sm:ml-auto justify-start sm:justify-end flex-wrap">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full bg-amber-100">
              <FileText className="w-3.5 h-3.5 text-amber-700" />
              <span className="text-[12px] sm:text-[13px] font-semibold text-amber-800">
                {t("Total")}: {stats.total || totalCount}
              </span>
            </div>

            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full bg-blue-100">
              <CheckCircle className="w-3.5 h-3.5 text-blue-700" />
              <span className="text-[12px] sm:text-[13px] font-semibold text-blue-800">
                {t("Success")}: {stats.success || successCount}
              </span>
            </div>

            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full bg-red-100">
              <Ban className="w-3.5 h-3.5 text-red-600" />
              <span className="text-[12px] sm:text-[13px] font-semibold text-red-700">
                {t("Failed")}: {stats.failed || failedCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      <AIProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        onSave={handleProfileSave}
      />

      <ConfirmDialog
        visible={showTopicSuggestConfirm}
        title={t("Missing Topic")}
        description={t("You haven't set a topic yet. Would you like to use AI Suggested Topics?")}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={() => {
          onUseSuggestedTopics();
          setShowTopicSuggestConfirm(false);
        }}
        onClose={() => setShowTopicSuggestConfirm(false)}
      />
    </div>
  );
};

export default PromptHeader;
